import { Fragment } from 'react'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { useTicketInflowScreen } from './hooks/useTicketInflowScreen'

// ── Outcome badge ─────────────────────────────────────────────────────────────
const OUTCOME_CONFIG = {
  SUCCESS: { label: 'Success', classes: 'bg-emerald-100 text-emerald-700' },
  FAILED: { label: 'Failed', classes: 'bg-red-100 text-red-700' },
  UNKNOWN: { label: 'Unknown', classes: 'bg-surface-container-high text-on-surface-variant' },
}

function OutcomeBadge({ outcome }) {
  const cfg = OUTCOME_CONFIG[outcome] || OUTCOME_CONFIG.UNKNOWN
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${cfg.classes}`}>
      {cfg.label}
    </span>
  )
}

const EVENT_LABELS = {
  OPEN: 'Open',
  UPDATE: 'Update',
  CUSTOMER_FEEDBACK: 'Feedback',
  CLOSE: 'Close',
}

export default function TicketInflowRegistryScreen() {
  const { state, actions } = useTicketInflowScreen()

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      {/* Header */}
      <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">
            Ticket Integration
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-on-surface-variant">
            All inbound ERP webhook events — track every ticket sync attempt, view details, and
            retry failed entries.
          </p>
        </div>

        {/* Sync Failed Tickets Button */}
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={actions.handleBulkRetry}
            disabled={state.isBulkRetrying}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-on-primary transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">
              {state.isBulkRetrying ? 'hourglass_empty' : 'sync'}
            </span>
            <span>{state.isBulkRetrying ? 'Syncing...' : 'Sync Failed Tickets'}</span>
          </button>
        </div>
      </header>

      {/* Alerts */}
      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      ) : null}
      {state.success ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {state.success}
        </div>
      ) : null}

      {/* Filters */}
      <section className="mb-6 flex flex-wrap gap-3">
        {/* Search */}
        <label className="flex flex-1 min-w-[200px] items-center gap-2 rounded-xl bg-surface-container-low px-4 py-2.5">
          <span className="material-symbols-outlined text-lg text-on-surface-variant">search</span>
          <input
            type="text"
            placeholder="Search ticket #, client…"
            value={state.search}
            onChange={(e) => actions.setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
          />
        </label>

        {/* Outcome filter */}
        <div className="w-48">
          <AppDropdown
            value={state.statusFilter}
            onChange={actions.setStatusFilter}
            triggerClassName="w-full rounded-xl bg-surface-container-low border-none px-4 py-2.5 text-sm font-semibold"
            options={[
              { value: 'all', label: 'All Outcomes' },
              { value: 'SUCCESS', label: 'Success' },
              { value: 'FAILED', label: 'Failed' },
            ]}
          />
        </div>
      </section>

      {/* Table */}
      <section className="overflow-hidden rounded-3xl bg-surface-container-low">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/30">
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Synced At
                </th>
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Ticket No
                </th>
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Event
                </th>
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Client
                </th>
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Assigned To
                </th>
                <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Outcome
                </th>
                <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {state.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-sm text-on-surface-variant">
                    Loading sync logs…
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-sm text-on-surface-variant">
                    No sync events found.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const isExpanded = state.expandedRowId === row.id
                  return (
                    <Fragment key={row.id}>
                      <tr className="transition-colors hover:bg-surface-container-lowest">
                        {/* Synced At */}
                        <td className="whitespace-nowrap px-5 py-4 text-xs text-on-surface-variant">
                          {row.syncedAt}
                        </td>

                        {/* Ticket No */}
                        <td className="px-5 py-4">
                          <span className="font-bold text-primary">{row.ticketNo}</span>
                        </td>

                        {/* Event */}
                        <td className="px-5 py-4">
                          <span className="rounded-full bg-surface-container-high px-2.5 py-0.5 text-xs font-bold uppercase text-on-surface">
                            {EVENT_LABELS[row.eventType] || row.eventType}
                          </span>
                        </td>

                        {/* Client */}
                        <td className="px-5 py-4 text-sm text-on-surface">{row.clientName}</td>

                        {/* Assigned To */}
                        <td className="px-5 py-4 text-sm text-on-surface">{row.assigneeName}</td>

                        {/* Outcome */}
                        <td className="px-5 py-4">
                          <OutcomeBadge outcome={row.outcome} />
                        </td>

                        {/* Details */}
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => actions.toggleExpandedRow(row.id)}
                            className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
                          >
                            {isExpanded ? 'Hide' : 'View'} Details
                            <span className="material-symbols-outlined text-sm">
                              {isExpanded ? 'expand_less' : 'chevron_right'}
                            </span>
                          </button>
                        </td>
                      </tr>

                      {/* Collapsible Details Row */}
                      {isExpanded ? (
                        <tr className="bg-surface-container-low">
                          <td colSpan={7} className="px-6 py-5">
                            <article className="rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-4">
                              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                                Sync / Ticket Details
                              </p>
                              <div className="rounded bg-surface-container-low p-3 text-xs text-on-surface">
                                {row.outcome === 'FAILED' ? (
                                  <div className="text-red-700">
                                    <p className="font-bold">Error Message:</p>
                                    <p className="mt-1 font-mono bg-red-50 p-2 rounded border border-red-200 whitespace-pre-wrap">
                                      {row.errorMessage || 'No error details found.'}
                                    </p>

                                    {row.canRetry && (
                                      <div className="mt-3">
                                        <button
                                          type="button"
                                          onClick={() => actions.retryLog(row.id)}
                                          disabled={state.retryingId === row.id}
                                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-on-primary hover:brightness-95 disabled:opacity-50"
                                        >
                                          <span className="material-symbols-outlined text-sm">
                                            {state.retryingId === row.id ? 'hourglass_empty' : 'replay'}
                                          </span>
                                          {state.retryingId === row.id ? 'Retrying…' : 'Retry Sync'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-emerald-700">
                                    <p className="font-bold">Sync Details:</p>
                                    <p className="mt-1 font-mono bg-emerald-50/50 p-2 rounded border border-emerald-200">
                                      Successfully synced to Task / Job:{' '}
                                      <span className="font-bold">{row.details}</span>
                                    </p>
                                  </div>
                                )}

                                {/* Ticket Details Grid */}
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-on-surface-variant border-t border-outline-variant/30 pt-3">
                                  <div>
                                    <span className="font-bold">Subject:</span>{' '}
                                    {row.raw.payload_subject || '-'}
                                  </div>
                                  <div>
                                    <span className="font-bold">Raised By:</span>{' '}
                                    {row.raw.payload_raised_by || '-'}
                                  </div>
                                  <div>
                                    <span className="font-bold">Priority:</span>{' '}
                                    {row.raw.payload_priority || 'Normal'}
                                  </div>
                                  <div>
                                    <span className="font-bold">Timestamp:</span>{' '}
                                    {row.raw.payload_timestamp || '-'}
                                  </div>
                                  <div className="col-span-1 md:col-span-2">
                                    <span className="font-bold">Description:</span>
                                    <p className="mt-1 whitespace-pre-wrap text-xs bg-surface-container-highest/20 p-2.5 rounded">
                                      {row.raw.payload_description || '-'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </article>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-6 py-5">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart}–{state.rangeEnd} of {state.totalCount} events
          </span>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Analytics footer */}
      <section className="mt-10">
        <h2 className="mb-5 text-2xl font-extrabold tracking-tight text-on-surface">
          Synchronization Health
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <article className="rounded-3xl bg-surface-container-low p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                Success Rate
              </p>
              <h3 className="text-3xl font-black text-primary">
                {Number(state.analytics.sync_success_rate || 0).toFixed(1)}%
              </h3>
            </div>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-surface-variant">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.max(4, Number(state.analytics.sync_success_rate || 0))}%` }}
              />
            </div>
          </article>
          <article className="rounded-3xl bg-surface-container-low p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                Tasks Created
              </p>
              <h3 className="text-3xl font-black text-primary">
                {Number(state.analytics.tasks_created || 0)}
              </h3>
            </div>
          </article>
          <article className="rounded-3xl bg-surface-container-low p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                Failures (30d)
              </p>
              <h3 className="text-3xl font-black text-red-500">
                {Number(state.analytics.sync_failures || 0)}
              </h3>
            </div>
          </article>
        </div>
      </section>
    </div>
  )
}
